/**
 * Shared helper functions for modifier implementations.
 *
 * Morph attribute helpers propagate shape keys through each modifier.
 * Edge key helper packs two vertex indices into a single number for fast map lookups.
 */

import * as THREE from "three/webgpu";
import { VERTEX_REMOVED, type VertexProvenance } from "../../wasm/editOps.ts";

// ---------------------------------------------------------------------------
// Provenance projection — forward-project a source-space per-vertex index
// set through the running provenance to the current geo's vertex space.
// `provenance.remap[oldIdx]` is the canonical way to project per-vertex
// index sets across accumulated topology ops (see apps/web/src/wasm/editOps.ts).
// ---------------------------------------------------------------------------

/**
 * Forward-project an owner-source-space vertex index set through `provenance`
 * to the vertex space of the current ModifierGeometry. Returns null when the
 * input is null/empty so callers can treat it as "no restriction".
 */
export function projectVertexGroupThroughProvenance(
  sourceIndices: Uint32Array | null | undefined,
  provenance: VertexProvenance,
): Set<number> | null {
  if (!sourceIndices || sourceIndices.length === 0) return null;
  const remap = provenance.remap;
  const out = new Set<number>();
  for (let i = 0; i < sourceIndices.length; i++) {
    const src = sourceIndices[i];
    if (src < remap.length) {
      const cur = remap[src];
      if (cur !== VERTEX_REMOVED) out.add(cur);
    }
  }
  return out;
}

/** Extract morph position arrays from a geometry. */
export function getMorphArrays(geo: THREE.BufferGeometry): Float32Array[] {
  const morphPos = geo.morphAttributes.position as
    | THREE.BufferAttribute[]
    | undefined;
  if (!morphPos) return [];
  return morphPos.map((attr) => attr.array as Float32Array);
}

/** Extract UV array from geometry, or null if absent. */
export function getUVs(geo: THREE.BufferGeometry): Float32Array | null {
  const uvAttr = geo.getAttribute("uv") as THREE.BufferAttribute | null;
  return uvAttr ? (uvAttr.array as Float32Array) : null;
}

/** Set morph attributes on a result geometry, copying metadata from source. */
export function setMorphArrays(
  result: THREE.BufferGeometry,
  source: THREE.BufferGeometry,
  arrays: Float32Array[],
): void {
  if (arrays.length === 0) return;
  result.morphAttributes.position = arrays.map(
    (arr) => new THREE.BufferAttribute(arr, 3),
  );
  result.morphTargetsRelative = source.morphTargetsRelative;
  if ((source.userData as Record<string, unknown>).morphNames) {
    (result.userData as Record<string, unknown>).morphNames = (
      source.userData as Record<string, unknown>
    ).morphNames;
  }
}

// ---------------------------------------------------------------------------
// Skin weight/index helpers for subdivision of SkinnedMesh geometry
// ---------------------------------------------------------------------------

export interface SkinData {
  skinIndex: Float32Array; // itemSize=4 per vertex
  skinWeight: Float32Array; // itemSize=4 per vertex
}

/** Extract skinIndex/skinWeight from geometry, or null if absent. */
export function getSkinData(geo: THREE.BufferGeometry): SkinData | null {
  const siAttr = geo.getAttribute("skinIndex") as THREE.BufferAttribute | null;
  const swAttr = geo.getAttribute("skinWeight") as THREE.BufferAttribute | null;
  if (!siAttr || !swAttr) return null;
  return {
    skinIndex: siAttr.array as Float32Array,
    skinWeight: swAttr.array as Float32Array,
  };
}

/** Set skinIndex/skinWeight on a result geometry. */
export function setSkinData(
  result: THREE.BufferGeometry,
  skinIndex: Float32Array,
  skinWeight: Float32Array,
): void {
  result.setAttribute("skinIndex", new THREE.BufferAttribute(skinIndex, 4));
  result.setAttribute("skinWeight", new THREE.BufferAttribute(skinWeight, 4));
}
